import React, { useMemo, useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  Chip,
  Card,
  CardContent,
  Divider,
  alpha,
  useTheme
} from '@mui/material';
import {
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ScatterChart,
  Scatter,
  Cell,
  Area,
  Legend,
  ComposedChart,
  Bar,
  ReferenceLine
} from 'recharts';
import {
  Schedule as ScheduleIcon,
  TrendingUp as TrendingUpIcon,
  EventNote as EventNoteIcon,
  Speed as SpeedIcon
} from '@mui/icons-material';
import { Release } from '../types';
import ShimmerCard from './ShimmerCard';

interface ReleaseTimelineProps {
  releases: Release[];
  onReleaseClick: (release: Release) => void;
  isLoading?: boolean;
}
const ReleaseTimeline: React.FC<ReleaseTimelineProps> = ({
  releases,
  onReleaseClick,
  isLoading = false
}) => {
  const theme = useTheme();
  const [hoveredRelease, setHoveredRelease] = useState<string | null>(null);

  const timelineData = useMemo(() => {
    // Sort releases by date
    const sortedReleases = [...releases].sort((a, b) => 
      new Date(a.releaseDate).getTime() - new Date(b.releaseDate).getTime()
    );

    return sortedReleases.map((release, index) => {
      const currentDate = new Date(release.releaseDate);
      const prevDate = index > 0 ? new Date(sortedReleases[index - 1].releaseDate) : null;
      const daysSinceLast = prevDate 
        ? Math.floor((currentDate.getTime() - prevDate.getTime()) / (1000 * 60 * 60 * 24))
        : 0;

      return {
        date: currentDate.toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric',
          year: 'numeric'
        }),
        shortDate: currentDate.toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric'
        }),
        timestamp: currentDate.getTime(),
        version: release.version,
        status: release.status,
        type: release.releaseType,
        index: index + 1,
        release: release,
        daysSinceLast,
        releaseVelocity: daysSinceLast > 0 ? 30 / daysSinceLast : 0, // Releases per month
        quarter: `Q${Math.floor(currentDate.getMonth() / 3) + 1} ${currentDate.getFullYear()}`,
        month: currentDate.toLocaleDateString('en-US', { month: 'short', year: '2-digit' })
      };
    });
  }, [releases]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Past':
        return '#4caf50';
      case 'Current':
        return '#2196f3';
      case 'Planned':
        return '#9e9e9e';
      default:
        return '#9e9e9e';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'Development':
        return '#ff9800';
      case 'Staging':
        return '#ffc107';
      case 'Production':
        return '#4caf50';
      default:
        return '#9e9e9e';
    }
  };

  // Calculate statistics
  const stats = useMemo(() => {
    if (timelineData.length === 0) return null;
    
    const avgDaysBetweenReleases = timelineData.length > 1
      ? timelineData.reduce((sum, item) => sum + item.daysSinceLast, 0) / (timelineData.length - 1)
      : 0;

    const byType = timelineData.reduce((acc, item) => {
      acc[item.type] = (acc[item.type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const byStatus = timelineData.reduce((acc, item) => {
      acc[item.status] = (acc[item.status] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return {
      total: timelineData.length,
      avgDaysBetweenReleases: Math.round(avgDaysBetweenReleases),
      byType,
      byStatus,
      firstRelease: timelineData[0].date,
      lastRelease: timelineData[timelineData.length - 1].date
    };
  }, [timelineData]);

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Paper 
          elevation={8}
          sx={{ 
            p: 2.5, 
            maxWidth: 350,
            background: `linear-gradient(135deg, ${alpha(getStatusColor(data.status), 0.1)} 0%, ${alpha(theme.palette.background.paper, 0.95)} 50%)`,
            border: `2px solid ${getStatusColor(data.status)}`,
            borderRadius: 2
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <EventNoteIcon sx={{ color: getStatusColor(data.status) }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {data.version}
            </Typography>
          </Box>
          
          <Divider sx={{ my: 1 }} />
          
          <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ScheduleIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">
                {data.date}
              </Typography>
            </Box>
            
            {data.daysSinceLast > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SpeedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  {data.daysSinceLast} days since last release
                </Typography>
              </Box>
            )}
          </Box>
          
          <Box sx={{ mt: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip 
              label={data.status} 
              size="small" 
              sx={{ 
                bgcolor: alpha(getStatusColor(data.status), 0.2),
                color: getStatusColor(data.status),
                fontWeight: 600
              }} 
            />
            <Chip 
              label={data.type} 
              size="small" 
              sx={{ 
                bgcolor: alpha(getTypeColor(data.type), 0.2),
                color: getTypeColor(data.type),
                fontWeight: 600
              }} 
            />
            <Chip 
              label={`#${data.index}`} 
              size="small" 
              variant="outlined"
            />
          </Box>
        </Paper>
      );
    }
    return null;
  };

  const CustomDot = (props: any) => {
    const { cx, cy, payload } = props;
    const isHovered = hoveredRelease === payload.release.id;
    const baseRadius = 8;
    const radius = isHovered ? baseRadius * 1.5 : baseRadius;
    
    return (
      <g>
        {/* Pulse effect for current release */}
        {payload.status === 'Current' && (
          <circle
            cx={cx}
            cy={cy}
            r={radius + 5}
            fill={getStatusColor(payload.status)}
            opacity={0.3}
          >
            <animate
              attributeName="r"
              from={radius}
              to={radius + 10}
              dur="2s"
              repeatCount="indefinite"
            />
            <animate
              attributeName="opacity"
              from="0.5"
              to="0"
              dur="2s"
              repeatCount="indefinite"
            />
          </circle>
        )}
        
        {/* Main dot */}
        <circle
          cx={cx}
          cy={cy}
          r={radius}
          fill={getStatusColor(payload.status)}
          stroke="#fff"
          strokeWidth={isHovered ? 3 : 2}
          style={{ 
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            filter: isHovered ? 'drop-shadow(0 0 6px rgba(0,0,0,0.4))' : 'none'
          }}
          onClick={() => onReleaseClick(payload.release)}
          onMouseEnter={() => setHoveredRelease(payload.release.id)}
          onMouseLeave={() => setHoveredRelease(null)}
        />
        
        {/* Inner highlight */}
        <circle
          cx={cx}
          cy={cy}
          r={radius / 2}
          fill="#fff"
          opacity={0.4}
          style={{ pointerEvents: 'none' }}
        />
      </g>
    );
  };

  if (isLoading) {
    return (
      <Box>
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
            <ShimmerCard variant="statistics" />
          </Box>
          <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
            <ShimmerCard variant="statistics" />
          </Box>
          <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
            <ShimmerCard variant="statistics" />
          </Box>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <ShimmerCard variant="timeline" />
          <ShimmerCard variant="timeline" />
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      {/* Statistics Overview */}
      {stats && (
        <Box 
          sx={{ 
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(2, 1fr)',  // 2x2 grid on mobile
              md: 'repeat(4, 1fr)'   // 1x4 grid on desktop
            },
            gap: 2,
            mb: 3
          }}
        >
          <Card 
            sx={{ 
              background: `linear-gradient(135deg, ${alpha('#2196f3', 0.1)} 0%, ${alpha(theme.palette.background.paper, 0.9)} 100%)`,
              border: `1px solid ${alpha('#2196f3', 0.2)}`,
              height: '100%'
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <EventNoteIcon sx={{ color: '#2196f3' }} />
                <Typography variant="h4" sx={{ fontWeight: 600, color: '#2196f3' }}>
                  {stats.total}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Total Releases
              </Typography>
            </CardContent>
          </Card>
          
          <Card 
            sx={{ 
              background: `linear-gradient(135deg, ${alpha('#ff9800', 0.1)} 0%, ${alpha(theme.palette.background.paper, 0.9)} 100%)`,
              border: `1px solid ${alpha('#ff9800', 0.2)}`,
              height: '100%'
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <SpeedIcon sx={{ color: '#ff9800' }} />
                <Typography variant="h4" sx={{ fontWeight: 600, color: '#ff9800' }}>
                  {stats.avgDaysBetweenReleases}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Avg. Days Between Releases
              </Typography>
            </CardContent>
          </Card>
          
          <Card 
            sx={{ 
              background: `linear-gradient(135deg, ${alpha('#4caf50', 0.1)} 0%, ${alpha(theme.palette.background.paper, 0.9)} 100%)`,
              border: `1px solid ${alpha('#4caf50', 0.2)}`,
              height: '100%'
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <TrendingUpIcon sx={{ color: '#4caf50' }} />
                <Typography variant="h4" sx={{ fontWeight: 600, color: '#4caf50' }}>
                  {stats.byStatus.Past || 0}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Completed Releases
              </Typography>
            </CardContent>
          </Card>
          
          <Card 
            sx={{ 
              background: `linear-gradient(135deg, ${alpha('#9e9e9e', 0.1)} 0%, ${alpha(theme.palette.background.paper, 0.9)} 100%)`,
              border: `1px solid ${alpha('#9e9e9e', 0.2)}`,
              height: '100%'
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <ScheduleIcon sx={{ color: '#9e9e9e' }} />
                <Typography variant="h4" sx={{ fontWeight: 600, color: '#9e9e9e' }}>
                  {stats.byStatus.Planned || 0}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Planned Releases
              </Typography>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Release Velocity Chart */}
      <Paper 
        sx={{ 
          p: { xs: 2, sm: 3 }, 
          mb: 3,
          background: `linear-gradient(180deg, ${alpha(theme.palette.success.main, 0.02)} 0%, ${theme.palette.background.paper} 100%)`
        }}
      >
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SpeedIcon />
          Release Velocity & Cadence
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Time between releases and release frequency patterns
        </Typography>

        {timelineData.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 5 }}>
            <Typography variant="body1" color="text.secondary">
              No releases to display
            </Typography>
          </Box>
        ) : (
          <Box sx={{ width: '100%', height: { xs: 250, sm: 300 } }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={timelineData.filter(d => d.daysSinceLast > 0)}>
              <defs>
                <linearGradient id="colorVelocity" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#ff9800" stopOpacity={0.8}/>
                  <stop offset="95%" stopColor="#ff9800" stopOpacity={0.1}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.2)} />
              <XAxis 
                dataKey="version" 
                angle={-45}
                textAnchor="end"
                height={80}
                tick={{ fontSize: 12 }}
                stroke={theme.palette.text.secondary}
              />
              <YAxis 
                label={{ 
                  value: 'Days Since Last Release', 
                  angle: -90, 
                  position: 'insideLeft',
                  style: { fill: theme.palette.text.secondary }
                }}
                stroke={theme.palette.text.secondary}
              />
              <Tooltip 
                content={({ active, payload }) => {
                  if (active && payload && payload.length) {
                    const data = payload[0].payload;
                    return (
                      <Paper sx={{ p: 2 }}>
                        <Typography variant="subtitle2">{data.version}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {data.daysSinceLast} days since previous release
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Released on {data.date}
                        </Typography>
                      </Paper>
                    );
                  }
                  return null;
                }}
              />
              <Bar 
                dataKey="daysSinceLast" 
                fill="url(#colorVelocity)" 
                name="Days Between Releases"
                radius={[8, 8, 0, 0]}
              >
                {timelineData.map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={entry.daysSinceLast > 60 ? '#f44336' : entry.daysSinceLast > 30 ? '#ff9800' : '#4caf50'}
                  />
                ))}
              </Bar>
              <Line
                type="monotone"
                dataKey="daysSinceLast"
                stroke="#ff5722"
                strokeWidth={2}
                dot={{ r: 4 }}
                name="Trend"
              />
            </ComposedChart>
          </ResponsiveContainer>
          </Box>
        )}
      </Paper>

      {/* Enhanced Timeline Chart */}
      <Paper 
        sx={{ 
          p: { xs: 2, sm: 3 }, 
          mb: 3,
          background: `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.02)} 0%, ${theme.palette.background.paper} 100%)`
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <EventNoteIcon />
              Release Timeline
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Interactive chronological view with release milestones
            </Typography>
          </Box>
        </Box>

        {timelineData.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 5 }}>
            <Typography variant="body1" color="text.secondary">
              No releases to display
            </Typography>
          </Box>
        ) : (
          <>
            <Box sx={{ width: '100%', height: { xs: 250, sm: 300, md: 350 } }}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={timelineData}>
                <defs>
                  <linearGradient id="colorIndex" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#2196f3" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#2196f3" stopOpacity={0.1}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.2)} />
                <XAxis 
                  dataKey="shortDate" 
                  angle={-45}
                  textAnchor="end"
                  height={80}
                  tick={{ fontSize: 12 }}
                  stroke={theme.palette.text.secondary}
                />
                <YAxis 
                  dataKey="index"
                  label={{ 
                    value: 'Release Number', 
                    angle: -90, 
                    position: 'insideLeft',
                    style: { fill: theme.palette.text.secondary }
                  }}
                  stroke={theme.palette.text.secondary}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                
                {/* Current release marker */}
                {timelineData.find(d => d.status === 'Current') && (
                  <ReferenceLine 
                    x={timelineData.find(d => d.status === 'Current')?.shortDate} 
                    stroke="#2196f3" 
                    strokeDasharray="5 5"
                    strokeWidth={2}
                    label={{ value: 'Current', position: 'top', fill: '#2196f3', fontWeight: 600 }}
                  />
                )}
                
                <Area
                  type="monotone"
                  dataKey="index"
                  fill="url(#colorIndex)"
                  stroke="none"
                  name="Progress"
                />
                <Line
                  type="monotone"
                  dataKey="index"
                  stroke="#2196f3"
                  strokeWidth={3}
                  dot={<CustomDot />}
                  name="Releases"
                  activeDot={{ r: 12 }}
                />
              </ComposedChart>
            </ResponsiveContainer>
            </Box>

            <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ 
                  width: 12, 
                  height: 12, 
                  borderRadius: '50%', 
                  bgcolor: '#4caf50',
                  boxShadow: `0 0 8px ${alpha('#4caf50', 0.5)}`
                }} />
                <Typography variant="caption" fontWeight={500}>Past</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ 
                  width: 12, 
                  height: 12, 
                  borderRadius: '50%', 
                  bgcolor: '#2196f3',
                  boxShadow: `0 0 8px ${alpha('#2196f3', 0.5)}`
                }} />
                <Typography variant="caption" fontWeight={500}>Current</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ 
                  width: 12, 
                  height: 12, 
                  borderRadius: '50%', 
                  bgcolor: '#9e9e9e',
                  boxShadow: `0 0 8px ${alpha('#9e9e9e', 0.5)}`
                }} />
                <Typography variant="caption" fontWeight={500}>Planned</Typography>
              </Box>
            </Box>
          </>
        )}
      </Paper>

      {/* Scatter plot by release type */}
      <Paper 
        sx={{ 
          p: { xs: 2, sm: 3 },
          background: `linear-gradient(180deg, ${alpha(theme.palette.warning.main, 0.02)} 0%, ${theme.palette.background.paper} 100%)`
        }}
      >
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TrendingUpIcon />
          Releases by Environment
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Distribution across Development, Staging, and Production over time
        </Typography>

        {timelineData.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 5 }}>
            <Typography variant="body1" color="text.secondary">
              No releases to display
            </Typography>
          </Box>
        ) : (
          <>
            <Box sx={{ width: '100%', height: { xs: 250, sm: 300, md: 350 } }}>
              <ResponsiveContainer width="100%" height="100%">
                <ScatterChart>
                <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.2)} />
                <XAxis 
                  dataKey="timestamp" 
                  domain={['dataMin', 'dataMax']}
                  tickFormatter={(timestamp) => new Date(timestamp).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                  type="number"
                  angle={-45}
                  textAnchor="end"
                  height={80}
                  tick={{ fontSize: 12 }}
                  stroke={theme.palette.text.secondary}
                  label={{ 
                    value: 'Release Date', 
                    position: 'insideBottom', 
                    offset: -5,
                    style: { fill: theme.palette.text.secondary }
                  }}
                />
                <YAxis 
                  dataKey="type" 
                  type="category"
                  allowDuplicatedCategory={false}
                  stroke={theme.palette.text.secondary}
                  tick={{ fontSize: 12 }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Scatter data={timelineData} name="Releases">
                  {timelineData.map((entry, index) => (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={getTypeColor(entry.type)}
                      style={{ cursor: 'pointer' }}
                      onClick={() => onReleaseClick(entry.release)}
                      onMouseEnter={() => setHoveredRelease(entry.release.id)}
                      onMouseLeave={() => setHoveredRelease(null)}
                    />
                  ))}
                </Scatter>
              </ScatterChart>
            </ResponsiveContainer>
            </Box>

            <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ 
                  width: 12, 
                  height: 12, 
                  borderRadius: '50%', 
                  bgcolor: '#ff9800',
                  boxShadow: `0 0 8px ${alpha('#ff9800', 0.5)}`
                }} />
                <Typography variant="caption" fontWeight={500}>Development</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ 
                  width: 12, 
                  height: 12, 
                  borderRadius: '50%', 
                  bgcolor: '#ffc107',
                  boxShadow: `0 0 8px ${alpha('#ffc107', 0.5)}`
                }} />
                <Typography variant="caption" fontWeight={500}>Staging</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ 
                  width: 12, 
                  height: 12, 
                  borderRadius: '50%', 
                  bgcolor: '#4caf50',
                  boxShadow: `0 0 8px ${alpha('#4caf50', 0.5)}`
                }} />
                <Typography variant="caption" fontWeight={500}>Production</Typography>
              </Box>
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default ReleaseTimeline;
